import {
  Box,
  Button,
  Fieldset,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  Separator,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Upload } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ConfirmDialog, ErrorState, LoadingState } from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import type { FeatureCode, SoundClipApi } from '../api/soundClipApi.types'
import { FEATURE_CODE_OPTIONS } from '../api/soundClipApi.types'
import {
  useDeleteSoundClip,
  useGetSoundClip,
  useGetTakenFeatureCodes,
  useUpdateSoundClip,
} from '../queries/soundClipQueries'
import { type SoundClipTranslateFn, text } from '../types/i18n.types'
import { validateAudioFile } from '../utils/audioFile'

const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

export interface SoundClipEditPageProps {
  api: SoundClipApi
  pbxId: number
  soundClipId: number
  onBack: () => void
  onDeleted?: () => void
  t?: TranslateFn | SoundClipTranslateFn
}

export const SoundClipEditPage: React.FC<SoundClipEditPageProps> = ({
  api,
  pbxId,
  soundClipId,
  onBack,
  onDeleted,
  t,
}) => {
  const {
    data: soundClip,
    isLoading,
    error,
  } = useGetSoundClip(api, soundClipId)
  const updateMutation = useUpdateSoundClip(api, pbxId)
  const deleteMutation = useDeleteSoundClip(api, pbxId)
  const { data: takenCodes = [] } = useGetTakenFeatureCodes(api, pbxId)

  const [name, setName] = useState('')
  const [featureCode, setFeatureCode] = useState<FeatureCode>(0)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | undefined>()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [playingAudio, setPlayingAudio] = useState(false)
  const [audioError, setAudioError] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (soundClip) {
      setName(soundClip.name)
      setFeatureCode(soundClip.feature_code)
    }
  }, [soundClip])

  // Stop playback on unmount; a detached Audio keeps playing after navigation.
  useEffect(() => {
    return () => {
      audioRef.current?.pause()
      audioRef.current = null
    }
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!name.trim()) return

      try {
        await updateMutation.mutateAsync({
          id: soundClipId,
          payload: {
            name: name.trim(),
            feature_code: featureCode,
            ...(audioFile ? { audio_file: audioFile } : {}),
          },
        })
        onBack()
      } catch {
        // Error surfaced via updateMutation.error in the form below.
      }
    },
    [name, featureCode, audioFile, soundClipId, updateMutation, onBack],
  )

  const handleDelete = useCallback(async () => {
    try {
      await deleteMutation.mutateAsync(soundClipId)
      setShowDeleteConfirm(false)
      onDeleted?.()
      onBack()
    } catch {
      // Error surfaced via deleteMutation.error in the form below.
    }
  }, [soundClipId, deleteMutation, onBack, onDeleted])

  const acceptFile = useCallback(
    (file: File) => {
      const error = validateAudioFile(file, t)
      if (error) {
        // Clear any previously-accepted file so the dropzone doesn't show a
        // valid file's name alongside the rejection error.
        setAudioFile(null)
        setFileError(error)
        return
      }
      setFileError(undefined)
      setAudioFile(file)
    },
    [t],
  )

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) acceptFile(file)
    },
    [acceptFile],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) acceptFile(file)
      // Reset so re-selecting the same file (after a rejection or Remove) still
      // fires onChange — the value is otherwise unchanged and the event won't fire.
      e.target.value = ''
    },
    [acceptFile],
  )

  const handlePlayCurrent = useCallback(() => {
    if (!soundClip) return
    // Prefer the presigned file_url; the raw location 403s once the bucket
    // stops allowing anonymous reads.
    const url = soundClip.file_url ?? soundClip.location
    if (!url) return

    if (playingAudio) {
      audioRef.current?.pause()
      audioRef.current = null
      setPlayingAudio(false)
      return
    }

    setAudioError(false)
    const audio = new Audio(url)
    audio.onended = () => setPlayingAudio(false)
    audio.onerror = () => {
      setPlayingAudio(false)
      setAudioError(true)
    }
    // play() rejects when the source can't load (missing/deleted audio);
    // catch it so it isn't an unhandled rejection and show the user why.
    audio.play().catch(() => {
      setPlayingAudio(false)
      setAudioError(true)
    })
    audioRef.current = audio
    setPlayingAudio(true)
  }, [soundClip, playingAudio])

  if (isLoading) {
    return (
      <LoadingState
        layout="alert"
        title={text(t, 'soundClips.edit.loading', 'Loading sound clip...')}
      />
    )
  }

  if (error || !soundClip) {
    return (
      <ErrorState
        layout="alert"
        description={
          error?.message ??
          text(t, 'soundClips.edit.notFound', 'Sound clip not found.')
        }
      />
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <HStack justify="space-between" mb={6}>
        <Flex
          direction={'row'}
          justifyContent={'flex-start'}
          alignItems={'center'}
          gap={4}
        >
          <Text
            fontSize="sm"
            color="fg.muted"
            cursor="pointer"
            _hover={{ color: 'fg', textDecoration: 'underline' }}
            onClick={onBack}
          >
            {text(t, 'soundClips.edit.backToList', '← Back to list')}
          </Text>
          <Heading size="lg" mb={0}>
            {text(t, 'soundClips.edit.title', 'Edit Sound Clip')}
          </Heading>
        </Flex>
        <Button
          size="sm"
          variant="outline"
          borderRadius="lg"
          colorPalette="error"
          onClick={() => setShowDeleteConfirm(true)}
        >
          {text(t, 'soundClips.edit.deleteButton', 'Delete Sound Clip')}
        </Button>
      </HStack>

      <Box as="form" onSubmit={handleSubmit} maxW="560px">
        <VStack gap={5} align="stretch">
          <Fieldset.Root>
            <Fieldset.Legend>
              {text(t, 'soundClips.edit.nameLabel', 'Name')}
            </Fieldset.Legend>
            <Input
              placeholder={text(
                t,
                'soundClips.edit.namePlaceholder',
                'Enter sound clip name',
              )}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Fieldset.Root>

          <Fieldset.Root>
            <Fieldset.Legend>
              {text(t, 'soundClips.edit.featureCodeLabel', 'Feature Code')}
            </Fieldset.Legend>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={featureCode}
                onChange={(e) =>
                  setFeatureCode(Number(e.target.value) as FeatureCode)
                }
              >
                {FEATURE_CODE_OPTIONS.map((opt) => {
                  // A code is "taken" only if another clip (not this one) has it.
                  const taken = takenCodes.find(
                    (tc) => tc.code === opt.value && tc.id !== soundClipId,
                  )
                  return (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={!!taken}
                    >
                      {taken
                        ? `${opt.label} (taken by ${taken.name})`
                        : opt.label}
                    </option>
                  )
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Fieldset.Root>

          <Separator />

          {(soundClip.location || soundClip.file_url) && (
            <Fieldset.Root>
              <Fieldset.Legend>
                {text(t, 'soundClips.edit.currentAudio', 'Current Audio')}
              </Fieldset.Legend>
              <Button
                size="sm"
                variant="outline"
                borderRadius="lg"
                onClick={handlePlayCurrent}
              >
                {playingAudio
                  ? text(t, 'soundClips.edit.stopAudio', 'Stop')
                  : text(
                      t,
                      'soundClips.edit.playCurrentAudio',
                      'Play Current Audio',
                    )}
              </Button>
              {audioError && (
                <Text mt={2} fontSize="sm" color="fg.error">
                  {text(
                    t,
                    'soundClips.edit.audioLoadError',
                    "This clip's audio couldn't be loaded — it may need to be re-recorded.",
                  )}
                </Text>
              )}
            </Fieldset.Root>
          )}

          <Fieldset.Root>
            <Fieldset.Legend>
              {text(t, 'soundClips.edit.audioFileLabel', 'Replace Audio File')}
            </Fieldset.Legend>
            <Box
              border="2px dashed"
              borderColor={
                isDragOver
                  ? 'blue.400'
                  : fileError
                    ? 'red.400'
                    : 'border.default'
              }
              borderRadius="lg"
              p={6}
              textAlign="center"
              cursor="pointer"
              bg={isDragOver ? 'blue.50' : 'bg.subtle'}
              transition="all 0.15s"
              _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e: React.DragEvent) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleFileDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/wav,audio/x-wav,audio/mpeg,.wav,.mp3"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {audioFile ? (
                <VStack gap={1}>
                  <Icon as={Upload} boxSize={6} color="brand.fg" />
                  <Text fontWeight="medium">{audioFile.name}</Text>
                  <Text fontSize="sm" color="fg.muted">
                    {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    borderRadius="lg"
                    colorPalette="error"
                    onClick={(e) => {
                      e.stopPropagation()
                      setAudioFile(null)
                      setFileError(undefined)
                    }}
                  >
                    Remove
                  </Button>
                </VStack>
              ) : (
                <VStack gap={1}>
                  <Icon as={Upload} boxSize={8} color="fg.muted" />
                  <Text>
                    {text(
                      t,
                      'soundClips.edit.audioFilePlaceholder',
                      'Drag & drop a new file to replace, or click to browse',
                    )}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {text(
                      t,
                      'soundClips.edit.audioFileAccepted',
                      'WAV, MP3 (max 25 MB)',
                    )}
                  </Text>
                </VStack>
              )}
            </Box>
            {fileError && (
              <Text fontSize="sm" color="fg.error" mt={2}>
                {fileError}
              </Text>
            )}
          </Fieldset.Root>

          <Separator />

          <HStack gap={6}>
            <Box>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                {text(t, 'soundClips.edit.createdLabel', 'Created')}
              </Text>
              <Text fontSize="sm">{formatDateTime(soundClip.created_at)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                {text(t, 'soundClips.edit.updatedLabel', 'Last Updated')}
              </Text>
              <Text fontSize="sm">{formatDateTime(soundClip.updated_at)}</Text>
            </Box>
          </HStack>

          {(updateMutation.isError || deleteMutation.isError) && (
            <Box
              role="alert"
              colorPalette="error"
              borderWidth="1px"
              borderColor="border.error"
              bg="colorPalette.subtle"
              borderRadius="md"
              p={3}
            >
              <Text fontSize="sm" color="fg.error">
                {(() => {
                  const err = updateMutation.error ?? deleteMutation.error
                  if (err instanceof Error) return err.message
                  return text(
                    t,
                    'soundClips.edit.actionFailed',
                    'Action failed. Please try again.',
                  )
                })()}
              </Text>
            </Box>
          )}

          <HStack gap={3} justify="flex-end" pt={2}>
            <Button variant="outline" borderRadius="lg" onClick={onBack}>
              {text(t, 'soundClips.edit.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              bg="brand.solid"
              color="brand.contrast"
              borderRadius="lg"
              _hover={{ bg: 'brand.emphasized' }}
              _active={{ bg: 'brand.emphasized' }}
              loading={updateMutation.isPending}
              loadingText={text(t, 'soundClips.edit.saving', 'Saving...')}
            >
              {text(t, 'soundClips.edit.submit', 'Save Changes')}
            </Button>
          </HStack>
        </VStack>
      </Box>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={text(
          t,
          'soundClips.list.deleteConfirmTitle',
          'Delete Sound Clip',
        )}
        description={`Are you sure you want to delete "${soundClip.name}"? This action cannot be undone.`}
        confirmLabel={text(t, 'soundClips.list.deleteConfirmButton', 'Delete')}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </Box>
  )
}
